'use client';

import classnames from 'classnames';

import type { BaseComponentProps } from '../../../../base-component.types';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../tooltip';
import type { MultipleChoiceQuestionAnswerType } from '../../../multiple-choice-question-answer/MultipleChoiceQuestionAnswer.types';
import type { MultipleChoiceQuestionType } from '../../../multiple-choice-question/MultipleChoiceQuestion.types';
import { preview } from '../../../utils';
import { AssessmentIndexItem } from './AssessmentIndexItem';

import styles from './AssessmentIndex.module.scss';

type QuestionEntry =
  | MultipleChoiceQuestionType
  | MultipleChoiceQuestionAnswerType;

type AssessmentIndexProps = BaseComponentProps & {
  questions: QuestionEntry[];
  fluid?: boolean;
  onSelect: (id: string, index: number) => void;
};

type Response = 'correct' | 'incorrect' | 'unanswered' | 'pending';

function getResponse(question: QuestionEntry): Response {
  if ('correctOptionIndex' in question) {
    if (question.selectedOptionIndex === 0) return 'unanswered';
    return question.selectedOptionIndex === question.correctOptionIndex
      ? 'correct'
      : 'incorrect';
  }
  return question.selectedOptionIndex > 0 ? 'pending' : 'unanswered';
}

function AssessmentIndex({
  style,
  className,
  questions,
  fluid = false,
  onSelect,
  testId,
}: AssessmentIndexProps) {
  return (
    <div
      style={style}
      className={classnames(styles['main'], className)}
      data-testid={testId}
    >
      <div
        className={classnames(styles['index'], fluid && styles['index--fluid'])}
      >
        {questions.map((question, index) => {
          const item = (
            <AssessmentIndexItem
              index={index}
              response={getResponse(question)}
              flagged={question.flagged ?? false}
              bookmarked={question.bookmarked ?? false}
              onClick={() => onSelect(question.id, index)}
            />
          );
          if (!question.subject) {
            return <div key={question.id}>{item}</div>;
          }
          return (
            <Tooltip key={question.id} placement="top" mouseOnly={true}>
              <TooltipTrigger asChild={true}>
                <div>{item}</div>
              </TooltipTrigger>
              <TooltipContent className={styles['tooltip']}>
                {preview(question.text)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

export { AssessmentIndex };
